import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutline, ErrorOutline, Sync } from '@material-ui/icons';

import SyncCard from './SyncCard';
import DiscountToggle from './DiscountToggle';
import CurrentStatus from './CurrentStatus';

import { useAPIV2 } from '../../yoello-lib/modules/API2/useAPIv2';
import APIExtensions from '../../yoello-lib/modules/API2/APIDefs';
import useSquareAuthChecker from './useSquareAuthChecker';
import { makeStyles } from '@material-ui/core';
import useCheckPermissions from '../../hooks/useCheckPermissions';
import { PERMISSIONS } from '../../const/permissions.const';
import DisabledActionWrapper from '../../yoello-lib/modules/components/DisabledActionWrapper';


const useStyles = makeStyles({
    reauthWrapper: {
        width: "100%",
        display: "flex",
        justifyContent: "center"
    }
})

const SquareSyncView = () => {
    const { t } = useTranslation();
    const classes = useStyles();

    const { isLoading: isAuthorising } = useSquareAuthChecker();

    const { 
        hasPermission: hasVenueManagement, 
        onFailedInteraction: onFailedVenueInteraction
    } = useCheckPermissions(PERMISSIONS.VENUE_MANAGEMENT);

    const { 
        hasPermission: hasMenuManagement, 
        onFailedInteraction: onFailedMenuInteraction
    } = useCheckPermissions(PERMISSIONS.MENU_MANAGEMENT);

    const { callAPI: callVenuesAPI } = useAPIV2(APIExtensions.resyncVenue, {
        onMountDisabled: true,
    });
    const { callAPI: callMenusAPI } = useAPIV2(APIExtensions.resyncMenu, {
        onMountDisabled: true,
    });
    const venuesMessages = {
        success: {
            icon: <CheckCircleOutline />,
            title: t("square.sync.venues.success.title"),
            description: t("square.sync.venues.success.description"),
        },
        error: {
            icon: <ErrorOutline />,
            title: t("square.sync.venues.fail.title"),
            description: t("square.sync.venues.fail.description"),
        },
        active: {
            icon: <Sync />,
            title: t("square.sync.venues.active.title"),
            description: t("square.sync.venues.active.description"),
        },
        hidden: {
            icon: null,
            title: '',
            description: '',
        },
    }

    const menusMessages = {
        success: {
            icon: <CheckCircleOutline />,
            title: t("square.sync.menus.success.title"),
            description: t("square.sync.menus.success.description"),
        },
        error: {
            icon: <ErrorOutline />,
            title: t("square.sync.menus.fail.title"),
            description: t("square.sync.menus.fail.description"),
        },
        active: {
            icon: <Sync />,
            title: t("square.sync.menus.active.title"),
            description: t("square.sync.menus.active.description"),
        },
        hidden: {
            icon: null,
            title: '',
            description: '',
        },
    }

    if(isAuthorising) {
        return (
            <div className={classes.reauthWrapper}>
                <CurrentStatus
                    isActive
                    isDeterminate={false}
                    percentComplete={50}
                    progress="active"
                    icon={<Sync />}
                    title={t("square.sync.reauthorise.reauth")}
                    description={t("square.sync.reauthorise.auth_time")}
                />
            </div>
        )
    }

    return (
        <>
            <DisabledActionWrapper
                disabled={!hasVenueManagement}
                onDisabledClick={onFailedVenueInteraction}
            >
                <SyncCard 
                    callAPI={callVenuesAPI}
                    title={t('square.sync.venues.title')}
                    description={t('square.sync.venues.description')}
                    buttonText={t('square.sync.venues.button')}
                    messages={venuesMessages}
                    query={venue => `?venue_id=${venue}`}
                    getStatusId={syncStatus => syncStatus.data.sync_status_id}
                    hasAddItemsToMenuSection={false}
                    disabled={!hasVenueManagement}
                />
            </DisabledActionWrapper>
            
            <DisabledActionWrapper
                disabled={!hasMenuManagement}
                onDisabledClick={onFailedMenuInteraction}
            >
                <SyncCard 
                    callAPI={callMenusAPI}
                    title={t('square.sync.menus.title')}
                    description={t('square.sync.menus.description')}
                    buttonText={t('square.sync.menus.button')}
                    messages={menusMessages}
                    query={venue => `?venue_id=${venue}`}
                    getStatusId={syncStatus => syncStatus.data.sync_status_id}
                    hasAddItemsToMenuSection={true}
                    disabled={!hasMenuManagement}
                />
            </DisabledActionWrapper>
            
            <DiscountToggle />
        </>
    )
}

export default SquareSyncView;